import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const PhotoUpload = ({ photos = [], onChange, maxPhotos = 5, acceptPDF = false, fieldId = 'photo-upload' }) => {
  const [previews, setPreviews] = useState([]);
  
  // Ensure photos is always an array
  const photoArray = Array.isArray(photos) ? photos : [];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit
    if (photoArray.length + files.length > maxPhotos) {
      toast.error(`Maksimum ${maxPhotos} dosya yükleyebilirsiniz`);
      return;
    }

    // Validate file types and sizes
    const validFiles = [];
    const invalidFiles = [];
    
    files.forEach(file => {
      // Check file type
      const isImage = file.type.startsWith('image/');
      const isPDF = acceptPDF && file.type === 'application/pdf';
      
      if (!isImage && !isPDF) {
        const allowedTypes = acceptPDF ? 'PNG, JPG, JPEG, PDF' : 'PNG, JPG, JPEG';
        invalidFiles.push(`${file.name} (sadece ${allowedTypes} dosyaları)`);
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (maksimum 5MB)`);
        return;
      }
      
      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Geçersiz dosyalar: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      // Create previews
      const newPreviews = [];
      const fileReaders = validFiles.map((file, index) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push({
              file,
              preview: reader.result,
              id: Date.now() + index
            });
            resolve();
          };
          reader.readAsDataURL(file);
        });
      });

      Promise.all(fileReaders).then(() => {
        const updatedPhotos = [...photoArray, ...validFiles];
        onChange(updatedPhotos);
        toast.success(`${validFiles.length} fotoğraf eklendi`);
      });
    }
  };

  const handleRemove = (index) => {
    const updatedPhotos = photoArray.filter((_, i) => i !== index);
    onChange(updatedPhotos);
    toast.success('Fotoğraf silindi');
  };

  return (
    <div className="space-y-4" data-testid="photo-upload-component">
      {/* Upload Button */}
      {photoArray.length < maxPhotos && (
        <div>
          <input
            type="file"
            id={fieldId}
            accept={acceptPDF ? "image/*,.pdf" : "image/*"}
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid={fieldId}
          />
          <label htmlFor={fieldId}>
            <Card className="border-2 border-dashed border-gray-300 hover:border-orange-500 transition-colors cursor-pointer p-8 text-center bg-gray-50 hover:bg-orange-50">
              <div className="flex flex-col items-center gap-3">
                <div className="bg-orange-100 p-4 rounded-full">
                  <Upload className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-700">
                    {acceptPDF ? 'Dosya Yükle' : 'Fotoğraf Yükle'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {photoArray.length} / {maxPhotos} {acceptPDF ? 'dosya' : 'fotoğraf'} yüklendi
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {acceptPDF ? 'PNG, JPG, JPEG, PDF' : 'PNG, JPG, JPEG'} - Maksimum 5MB - En fazla {maxPhotos} {acceptPDF ? 'dosya' : 'fotoğraf'}
                  </p>
                </div>
              </div>
            </Card>
          </label>
        </div>
      )}

      {/* Photo Grid */}
      {photoArray.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {photoArray.map((photo, index) => {
            // Check if it's a PDF
            let isPDF = false;
            let previewUrl;
            let fileName;
            
            if (typeof photo === 'string') {
              // Already a string (base64 or URL)
              previewUrl = photo;
              isPDF = photo.startsWith('data:application/pdf');
            } else if (photo instanceof File) {
              // File object - create blob URL
              previewUrl = URL.createObjectURL(photo);
              isPDF = photo.type === 'application/pdf';
              fileName = photo.name;
            } else {
              // Unknown type - skip rendering
              console.warn('Unknown photo type:', photo);
              return null;
            }
            
            return (
              <Card
                key={index}
                className="relative group overflow-hidden aspect-square"
                data-testid={`photo-preview-${index}`}
              >
                {isPDF ? (
                  // PDF Preview
                  <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                    <FileText className="h-12 w-12 text-red-600 mb-2" />
                    <p className="text-xs text-gray-700 px-2 text-center break-all">{fileName || 'PDF Document'}</p>
                  </div>
                ) : (
                  // Image Preview
                  <img
                    src={previewUrl}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                
                {/* Overlay with delete button */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemove(index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${index}`}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Sil
                  </Button>
                </div>
                
                {/* Photo number badge */}
                <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {photoArray.length === 0 && (
        <div className="text-center py-4 text-gray-500 text-sm">
          Henüz fotoğraf yüklenmedi
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
